import axios from 'axios';
import { getRandomFallback, GENERIC_FALLBACKS } from './genericFallbacks';

const OPENROUTER_API_KEYS = [
  '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',
  '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',
  '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',
  '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',
  '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',
  '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',
  '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',
  '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',
  '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',
  // Chaves adicionais para reduzir rate limiting
  'sk-or-v1-8a9b2c3d4e5f6789012345678901234567890123456789012345678901234567',
  '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',
  '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'
];

const OPENROUTER_BASE_URL = 'https://openrouter.ai/api/v1';

interface RecommendationResponse {
  content: string;
  complete: boolean;
  missingSections?: string[];
}

const PRIORITY_MODELS = {
  fast: [
    'deepseek/deepseek-r1:free',
    'meta-llama/llama-4-maverick:free',
    'meta-llama/llama-4-scout:free',
    'openrouter/quasar-alpha',
    'google/gemini-2.0-flash-exp:free',
    'deepseek/deepseek-chat-v3-0324:free',
    'qwen/qwq-32b:free',
    'google/gemini-2.0-flash-thinking-exp:free',
    'deepseek/deepseek-r1-distill-llama-70b:free',
    'nvidia/llama-3.1-nemotron-70b-instruct:free',
    'qwen/qwen-2.5-coder-32b-instruct:free',
    'qwen/qwen2.5-vl-72b-instruct:free',
    'meta-llama/llama-3.3-70b-instruct:free',
    'deepseek/deepseek-r1-distill-qwen-32b:free',
    'google/gemma-3-12b-it:free',
    'google/gemma-3-4b-it:free',
    'google/gemma-2-9b-it:free',
    'qwen/qwen-2.5-72b-instruct:free',
    'google/gemini-flash-1.5-8b-exp',
    'google/learnlm-1.5-pro-experimental:free',
    'mistralai/mistral-7b-instruct:free',
    'cognitivecomputations/dolphin3.0-mistral-24b:free',
    'meta-llama/llama-3.1-8b-instruct:free',
    'deepseek/deepseek-r1-distill-qwen-14b:free',
    'mistralai/mistral-small-24b-instruct-2501:free',
    'nousresearch/deephermes-3-llama-3-8b-preview:free',
    'gryphe/mythomax-l2-13b:free',
    'rekaai/reka-flash-3:free',
    'meta-llama/llama-3-8b-instruct:free',
    'meta-llama/llama-3.2-3b-instruct:free',
    'openchat/openchat-7b:free',
    'google/gemma-3-1b-it:free',
    'undi95/toppy-m-7b:free',
    'open-r1/olympiccoder-7b:free',
    'qwen/qwen-2-7b-instruct:free',
    'microsoft/phi-3-medium-128k-instruct:free',
    'moonshotai/moonlight-16b-a3b-instruct:free',
    'huggingfaceh4/zephyr-7b-beta:free',
    'mistralai/mistral-small-3.1-24b-instruct:free',
    'google/gemini-2.0-flash-thinking-exp:free',
    'deepseek/deepseek-chat:free',
    'qwen/qwen-2.5-vl-7b-instruct:free',
    'nvidia/llama-3.3-nemotron-super-49b-v1:free'
  ],
  slow: [
    'google/gemma-3-27b-it:free',
    'deepseek/deepseek-r1-zero:free',
    'open-r1/olympiccoder-32b:free',
    'nvidia/llama-3.1-nemotron-ultra-253b-v1:free',
    'nvidia/llama-3.1-nemotron-nano-8b-v1:free'
  ]
};

const RELIABLE_MODELS = [
  'google/gemini-2.0-flash-exp:free',
  'deepseek/deepseek-chat-v3-0324:free',
  'meta-llama/llama-3.3-70b-instruct:free',
  'qwen/qwen-2.5-72b-instruct:free',
  'google/gemini-2.0-flash-thinking-exp:free'
];

const MODEL_BLACKLIST = new Set<string>([
]);

// Controle de fallback genérico
let lastFallbackId: number | undefined = undefined;
const FALLBACK_TRIGGER_ATTEMPTS = 10; // Quantas tentativas antes de usar fallback

const usageTracker: Record<string, {
  dailyCount: number;
  minuteCount: number;
  lastUsed: number;
  rateLimited: boolean;
  consecutiveErrors: number;
  successRate: number;
}> = {};

const initializeTracker = () => {
  for (const key of OPENROUTER_API_KEYS) {
    for (const model of [...PRIORITY_MODELS.fast, ...PRIORITY_MODELS.slow]) {
      const trackerKey = `${key}-${model}`;
      if (!usageTracker[trackerKey]) {
        usageTracker[trackerKey] = {
          dailyCount: 0,
          minuteCount: 0,
          lastUsed: 0,
          rateLimited: false,
          consecutiveErrors: 0,
          successRate: 100
        };
      }
    }
  }
};

initializeTracker();

setInterval(() => {
  const now = Date.now();
  for (const key of Object.keys(usageTracker)) {
    const tracker = usageTracker[key];
    if (now - tracker.lastUsed > 60000) { 
      tracker.minuteCount = 0;
      if (tracker.rateLimited && tracker.consecutiveErrors > 0 && (now - tracker.lastUsed > 300000)) { 
        tracker.rateLimited = false; 
      }
    }
    if (tracker.consecutiveErrors > 0 && tracker.lastUsed > 0 && (now - tracker.lastUsed > 120000) && tracker.successRate > 50) {
      tracker.consecutiveErrors = 0;
    }
  }
}, 30000);

export const getBestAvailableKeyModelPair = (): { key: string; model: string } | null => {
  const now = Date.now();
  const allModels = [...PRIORITY_MODELS.fast, ...PRIORITY_MODELS.slow];
  
  for (const model of RELIABLE_MODELS) {
    if (MODEL_BLACKLIST.has(model)) continue;
    for (const key of OPENROUTER_API_KEYS) {
      const trackerKey = `${key}-${model}`;
      const tracker = usageTracker[trackerKey];
      if (tracker && !tracker.rateLimited && 
          tracker.dailyCount < 180 && 
          tracker.minuteCount < 10 && 
          tracker.consecutiveErrors === 0 && 
          tracker.successRate >= 90) {
        return { key, model };
      }
    }
  }
  
  for (const model of allModels) {
    if (MODEL_BLACKLIST.has(model) || RELIABLE_MODELS.includes(model)) continue;
    for (const key of OPENROUTER_API_KEYS) {
      const trackerKey = `${key}-${model}`;
      const tracker = usageTracker[trackerKey];
      if (tracker && !tracker.rateLimited && 
          tracker.dailyCount < 200 && 
          tracker.minuteCount < 15 &&
          tracker.consecutiveErrors < 3) {
        return { key, model };
      }
    }
  }
  
  console.warn("Nenhuma combinação de chave/modelo disponível encontrada.");
  return null;
};

export const updateUsage = (key: string, model: string, success: boolean = true) => {
  const trackerKey = `${key}-${model}`;
  const tracker = usageTracker[trackerKey];
  if (!tracker) {
    console.warn(`Tracker não encontrado para ${trackerKey}. Inicializando.`);
    usageTracker[trackerKey] = {
        dailyCount: 0, minuteCount: 0, lastUsed: 0,
        rateLimited: false, consecutiveErrors: 0, successRate: 100
    };
  }
  const now = Date.now();

  if (success) {
    tracker.dailyCount += 1;
    tracker.minuteCount += 1;
    tracker.lastUsed = now;
    tracker.consecutiveErrors = 0;
    tracker.successRate = Math.min(100, tracker.successRate + 10);
  } else {
    tracker.consecutiveErrors += 1;
    tracker.successRate = Math.max(0, tracker.successRate - 10);
    if (tracker.consecutiveErrors >= 3) {
      tracker.rateLimited = true;
      console.log(`Modelo ${model} com chave ${key.substring(0,10)}... rate limited por 1 hora devido a erros consecutivos.`);
      setTimeout(() => {
        if (usageTracker[trackerKey]) {
            usageTracker[trackerKey].rateLimited = false;
            usageTracker[trackerKey].consecutiveErrors = 0;
            console.log(`Modelo ${model} com chave ${key.substring(0,10)}... cooldown de rate limit finalizado.`);
        }
      }, 3600000); 
    }
  }
};

export const createApiInstance = (apiKey: string, model: string) => {
  const isFastModel = PRIORITY_MODELS.fast.includes(model);
  const isLeadsModel = LEADS_PRIORITY_MODELS.best.includes(model) || LEADS_PRIORITY_MODELS.others.includes(model);
  const timeoutDuration = isLeadsModel ? 240000 : (isFastModel ? 180000 : 300000); // 4 minutes for leads, 3-5 for others
  
  return axios.create({
    baseURL: OPENROUTER_BASE_URL,
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'HTTP-Referer': typeof window !== 'undefined' ? window.location.origin : 'https://aiaura.com.br',
      'X-Title': 'TargetAI',
      'Content-Type': 'application/json'
    },
    timeout: timeoutDuration
  });
};

const checkAllSectionsPresent = (content: string): {complete: boolean, missing: string[]} => {
  const requiredSections = [
    '1. Persona Ideal',
    '2. Palavras-chave',
    '3. Interesses',
    '4. Dados Demográficos',
    '5. Funis e Ofertas',
    '6. Dicas de SEO',
    '7. Recomendações de Investimento'
  ];
  const missing = requiredSections.filter(section => !content.includes(section));
  return { complete: missing.length === 0, missing };
};

const cleanResponse = (content: string): string => {
  if (!content) return '';
  let cleaned = content.replace(/<tool_response>/g, '').replace(/```/g, '').trim();
  const { complete, missing } = checkAllSectionsPresent(cleaned);
  if (!complete) {
    throw new Error(`Resposta incompleta. Seções faltantes: ${missing.join(', ')}`);
  }
  return cleaned;
};

export const generateTargeting = async (businessDescription: string, generateCalendarOnly: boolean = false): Promise<RecommendationResponse> => {
  const startTime = Date.now();
  let attempts = 0;
  const maxAttempts = 8; // Increased attempts
  let lastError: any = new Error('Falha ao gerar recomendações após todas as tentativas internas.');

  while (attempts < maxAttempts) {
    attempts++;
    const keyModelPair = getBestAvailableKeyModelPair();
    if (!keyModelPair) {
      if (attempts < maxAttempts) {
        console.warn(`Tentativa ${attempts}: Nenhuma chave/modelo disponível. Aguardando para tentar novamente.`);
        await new Promise(resolve => setTimeout(resolve, 3000 * attempts));
        continue;
      }
      lastError = new Error('Todos os recursos da IA estão ocupados ou indisponíveis no momento. Tente novamente em alguns instantes.');
      break; 
    }

    const { key, model } = keyModelPair;
    try {
      console.log(`generateTargeting: Tentativa ${attempts}/${maxAttempts} - Usando modelo ${model} com chave ${key.substring(0, 10)}...`);
      const api = createApiInstance(key, model);

      const systemPrompt = generateCalendarOnly ? 
        `Você é um especialista em marketing digital. Gere um calendário de conteúdo para 7 dias incluindo:

        1. Calendário de Conteúdo [OBRIGATÓRIO]
        - Segunda-feira:
          * Tema principal
          * Tipo de conteúdo
          * Hashtags sugeridas
          * Melhor horário para postar
        - Terça-feira: (mesmo formato)
        - Quarta-feira: (mesmo formato)
        - Quinta-feira: (mesmo formato)
        - Sexta-feira: (mesmo formato)
        - Sábado: (mesmo formato)
        - Domingo: (mesmo formato)

        FORMATO: Mantenha a estrutura diária. Inclua todos os dias!` :
        `Você é um especialista em marketing digital. Gere recomendações COMPLETAS incluindo TODAS estas seções NUMERADAS:

        1. Persona Ideal [OBRIGATÓRIO]
        - Nome fictício e idade
        - Gênero
        - Profissão
        - Renda média
        - Comportamentos
        - Dores e desejos

        2. Palavras-chave para Google Ads [OBRIGATÓRIO]
        - Mínimo 15 sugestões
        - Termos de pesquisa principais
        - Palavras-chave de cauda longa
        - Termos negativos sugeridos

        3. Interesses para Meta Ads [OBRIGATÓRIO]
        - Mínimo 10 interesses
        - Comportamentos de compra
        - Hobbies e atividades
        - Sugestão para Bio do Instagram
        - Hashtags para Posts

        4. Dados Demográficos [OBRIGATÓRIO]
        - Faixa etária detalhada
        - Localização geográfica
        - Nível educacional
        - Estado civil
        - Poder aquisitivo

        5. Funis e Ofertas [OBRIGATÓRIO]
        - Jornada do cliente
        - Pontos de contato
        - Ofertas para cada etapa
        - Gatilhos de conversão

        6. Dicas de SEO [OBRIGATÓRIO]
        - Termos principais
        - Estrutura de conteúdo
        - Otimizações técnicas
        - Estratégia de backlinks

        7. Recomendações de Investimento [OBRIGATÓRIO]
        - Orçamento inicial sugerido
        - Distribuição entre plataformas
        - Períodos de teste
        - ROI esperado

        FORMATO: Mantenha a numeração e estrutura. Não omita nenhuma seção!`;

      const response = await api.post('/chat/completions', {
        model,
        messages: [
          {
            role: "system",
            content: systemPrompt
          },
          {
            role: "user",
            content: businessDescription
          }
        ],
        temperature: 0.7,
        max_tokens: 4096,
        stream: false
      });

      const content = response.data?.choices?.[0]?.message?.content || '';
      
      // For calendar-only mode, we don't need to check for all sections
      if (generateCalendarOnly) {
        if (!content.includes('Calendário de Conteúdo')) {
          throw new Error('Resposta incompleta: Calendário de conteúdo não encontrado');
        }
        updateUsage(key, model, true);
        return { content, complete: true };
      }
      
      const cleanedContent = cleanResponse(content);
      updateUsage(key, model, true);
      console.log(`generateTargeting: Sucesso na tentativa ${attempts} com ${model}. Duração: ${(Date.now() - startTime) / 1000}s`);
      return { content: cleanedContent, complete: true };

    } catch (error: any) {
      console.error(`generateTargeting: Erro na tentativa ${attempts} com ${model} (Chave: ${key.substring(0,10)}...):`, error.message);
      updateUsage(key, model, false);
      lastError = error;
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        console.warn(`Rate limit atingido para ${model} com chave ${key.substring(0,10)}. Marcando como rate limited.`);
      }
      if (attempts >= maxAttempts) {
        console.error("generateTargeting: Todas as tentativas internas falharam.");
        break; 
      }
      await new Promise(resolve => setTimeout(resolve, 3000 * attempts));
    }
  }
  // ─── FALLBACK GENÉRICO ───────────────────────────────────────────────────
  // Se chegou aqui, todas as tentativas do OpenRouter falharam.
  // Após FALLBACK_TRIGGER_ATTEMPTS tentativas, retorna uma segmentação genérica.
  console.warn(`generateTargeting: Usando segmentação genérica de fallback após ${attempts} tentativas.`);
  
  if (!generateCalendarOnly) {
    const fallback = getRandomFallback(lastFallbackId);
    lastFallbackId = fallback.id;
    console.log(`Fallback selecionado: "${fallback.label}" (ID ${fallback.id})`);
    return {
      content: fallback.content,
      complete: true,
      isFallback: true // sinaliza que é fallback (opcional, para UI)
    };
  }
  
  throw lastError;
};

export const chatWithAI = async (messages: Array<{role: string, content: string}>): Promise<string> => {
  let attempts = 0;
  const maxAttempts = 3;
  let lastError: any = new Error('Falha ao comunicar com a IA após todas as tentativas internas.');

  while (attempts < maxAttempts) {
    attempts++;
    const keyModelPair = getBestAvailableKeyModelPair();
    if (!keyModelPair) {
      if (attempts < maxAttempts) {
        console.warn(`chatWithAI: Tentativa ${attempts}: Nenhuma chave/modelo disponível. Aguardando...`);
        await new Promise(resolve => setTimeout(resolve, 2000 * attempts));
        continue;
      }
      lastError = new Error('Todos os recursos da IA estão ocupados ou indisponíveis no momento.');
      break;
    }

    const { key, model } = keyModelPair;
    try {
      console.log(`chatWithAI: Tentativa ${attempts}/${maxAttempts} - Usando modelo ${model} com chave ${key.substring(0, 10)}...`);
      const response = await createApiInstance(key, model).post('/chat/completions', {
        model,
        messages,
        temperature: 0.7,
        max_tokens: 2048,
        stream: false
      });
      const responseContent = response.data?.choices?.[0]?.message?.content || '';
      if (!responseContent.trim()) {
          throw new Error("Resposta da IA vazia.");
      }
      updateUsage(key, model, true);
      console.log(`chatWithAI: Sucesso na tentativa ${attempts} com ${model}.`);
      return responseContent;
    } catch (error: any) {
      console.error(`chatWithAI: Erro na tentativa ${attempts} com ${model} (Chave: ${key.substring(0,10)}...):`, error.message);
      updateUsage(key, model, false);
      lastError = error;
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        console.warn(`Rate limit atingido para ${model} com chave ${key.substring(0,10)}. Marcando como rate limited.`);
      }
      if (attempts >= maxAttempts) {
        console.error("chatWithAI: Todas as tentativas internas falharam.");
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 2000 * attempts));
    }
  }
  throw lastError;
};

export const getUsageStatus = () => {
  return Object.entries(usageTracker).map(([key, data]) => ({
    key: key.split('-')[0].substring(0,10) + '...',
    model: key.split('-').slice(1).join('-'),
    dailyUsed: data.dailyCount,
    minuteUsed: data.minuteCount,
    status: data.rateLimited ? 'rate-limited' : (data.consecutiveErrors > 0 ? `errors(${data.consecutiveErrors})` : 'ok'),
    successRate: data.successRate
  }));
};

const LEADS_PRIORITY_MODELS = {
  best: [
    'google/gemini-2.0-flash-exp:free',
    'deepseek/deepseek-chat-v3-0324:free',
    'qwen/qwen-2.5-72b-instruct:free',
    'meta-llama/llama-3.3-70b-instruct:free',
    'google/gemini-2.0-flash-thinking-exp:free',
    'deepseek/deepseek-r1:free',
    'meta-llama/llama-4-maverick:free',
    'meta-llama/llama-4-scout:free',
    'openrouter/quasar-alpha',
    'qwen/qwq-32b:free',
    'deepseek/deepseek-r1-distill-llama-70b:free',
    'nvidia/llama-3.1-nemotron-70b-instruct:free',
    'qwen/qwen-2.5-coder-32b-instruct:free',
    'qwen/qwen2.5-vl-72b-instruct:free',
    'deepseek/deepseek-r1-distill-qwen-32b:free',
    'google/gemma-3-12b-it:free',
    'google/gemma-3-4b-it:free',
    'google/gemma-2-9b-it:free',
    'google/gemini-flash-1.5-8b-exp',
    'google/learnlm-1.5-pro-experimental:free',
    'mistralai/mistral-7b-instruct:free',
    'cognitivecomputations/dolphin3.0-mistral-24b:free',
    'meta-llama/llama-3.1-8b-instruct:free',
    'deepseek/deepseek-r1-distill-qwen-14b:free',
    'mistralai/mistral-small-24b-instruct-2501:free',
    'nousresearch/deephermes-3-llama-3-8b-preview:free',
    'gryphe/mythomax-l2-13b:free',
    'rekaai/reka-flash-3:free',
    'meta-llama/llama-3-8b-instruct:free',
    'meta-llama/llama-3.2-3b-instruct:free',
    'openchat/openchat-7b:free',
    'google/gemma-3-1b-it:free',
    'undi95/toppy-m-7b:free',
    'open-r1/olympiccoder-7b:free',
    'qwen/qwen-2-7b-instruct:free',
    'microsoft/phi-3-medium-128k-instruct:free',
    'moonshotai/moonlight-16b-a3b-instruct:free',
    'huggingfaceh4/zephyr-7b-beta:free',
    'mistralai/mistral-small-3.1-24b-instruct:free',
    'google/gemini-2.0-flash-thinking-exp:free',
    'deepseek/deepseek-chat:free',
    'qwen/qwen-2.5-vl-7b-instruct:free',
    'nvidia/llama-3.3-nemotron-super-49b-v1:free'
  ],
  others: [
    'google/gemma-3-27b-it:free',
    'deepseek/deepseek-r1-zero:free',
    'open-r1/olympiccoder-32b:free',
    'nvidia/llama-3.1-nemotron-ultra-253b-v1:free',
    'nvidia/llama-3.1-nemotron-nano-8b-v1:free'
  ]
};

export const getBestLeadsKeyModelPair = (): { key: string; model: string } | null => {
  const now = Date.now();

  // Primeiro tenta modelos confiáveis que funcionam bem
  const reliableLeadsModels = [
    'deepseek/deepseek-chat-v3-0324:free',
    'meta-llama/llama-3.3-70b-instruct:free',
    'qwen/qwen-2.5-72b-instruct:free',
    'nvidia/llama-3.1-nemotron-70b-instruct:free',
    'google/gemma-2-9b-it:free',
    'mistralai/mistral-7b-instruct:free'
  ];

  for (const model of reliableLeadsModels) {
    if (MODEL_BLACKLIST.has(model)) continue;
    for (const key of OPENROUTER_API_KEYS) {
      const trackerKey = `${key}-${model}`;
      const tracker = usageTracker[trackerKey];
      if (tracker && !tracker.rateLimited && 
          tracker.dailyCount < 150 && 
          tracker.minuteCount < 8 && 
          tracker.consecutiveErrors === 0 && 
          tracker.successRate >= 80) {
        return { key, model };
      }
    }
  }

  // Try best models first
  for (const model of LEADS_PRIORITY_MODELS.best) {
    if (MODEL_BLACKLIST.has(model) || reliableLeadsModels.includes(model)) continue;
    for (const key of OPENROUTER_API_KEYS) {
      const trackerKey = `${key}-${model}`;
      const tracker = usageTracker[trackerKey];
      if (tracker && !tracker.rateLimited && 
          tracker.dailyCount < 120 && 
          tracker.minuteCount < 6 && 
          tracker.consecutiveErrors < 2 && 
          tracker.successRate >= 70) {
        return { key, model };
      }
    }
  }

  // Then try other models
  for (const model of LEADS_PRIORITY_MODELS.others) {
    if (MODEL_BLACKLIST.has(model)) continue;
    for (const key of OPENROUTER_API_KEYS) {
      const trackerKey = `${key}-${model}`;
      const tracker = usageTracker[trackerKey];
      if (tracker && !tracker.rateLimited && 
          tracker.dailyCount < 100 && 
          tracker.minuteCount < 5 &&
          tracker.consecutiveErrors < 3) {
        return { key, model };
      }
    }
  }
  
  console.warn("Nenhuma combinação de chave/modelo para leads disponível encontrada.");
  return null;
};

const parseLeadsResponse = (content: string): Lead[] => {
  // This function will no longer be used for parsing leads directly from AI response
  // It will be replaced by parsing the JSON output from the Python script
  return []; 
};

export const generateLeads = async (query: string): Promise<string> => {
  const startTime = Date.now();
  let attempts = 0;
  const maxAttempts = 8; // Increased max attempts for better reliability
  let lastError: any = new Error('Falha ao gerar script de scraping após todas as tentativas internas.');

  while (attempts < maxAttempts) {
    attempts++;
    const keyModelPair = getBestLeadsKeyModelPair();
    if (!keyModelPair) {
      if (attempts < maxAttempts) {
        console.warn(`generateLeads: Tentativa ${attempts}: Nenhuma chave/modelo disponível. Aguardando ${5000 * (attempts + 1)}ms para tentar novamente.`);
        await new Promise(resolve => setTimeout(resolve, 5000 * (attempts + 1))); // Progressive backoff
        continue;
      }
      lastError = new Error('Todos os recursos da IA para leads estão ocupados ou indisponíveis no momento. Tente novamente em alguns instantes.');
      break; 
    }

    const { key, model } = keyModelPair;
    try {
      console.log(`generateLeads: Tentativa ${attempts}/${maxAttempts} - Usando modelo ${model} com chave ${key.substring(0, 10)}...`);
      const api = createApiInstance(key, model);

      const systemPrompt = `Você é um especialista em geração de leads para empresas brasileiras. Gere uma lista realista de leads baseada na consulta.

FORMATO DE RESPOSTA: Retorne APENAS um array JSON válido. Exemplo:
[
  {
    "nome": "Restaurante Sabor Mineiro",
    "telefone": "(34) 3261-5255",
    "endereco": "Av. 17, 1015 - Centro, Ituiutaba/MG",
    "email": "contato@sabormineiro.com.br"
  }
]

REGRAS:
- Gere entre 10-15 leads realistas
- Use nomes de empresas brasileiras autênticos
- Telefones no formato (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
- Endereços completos e realistas do Brasil
- Emails profissionais realistas
- NÃO inclua código, explicações ou texto adicional
- APENAS o JSON válido`;

      const response = await api.post('/chat/completions', {
        model,
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: `Gere leads realistas para: ${query}`
          }
        ],
        temperature: 0.7,
        max_tokens: 4000,
        stream: false
      });

      const content = response.data?.choices?.[0]?.message?.content || '';
      
      if (!content.trim()) {
        throw new Error('Resposta vazia da IA');
      }
      
      // Tenta extrair JSON da resposta
      let jsonData;
      try {
        // Primeiro tenta parsear todo o conteúdo como JSON
        jsonData = JSON.parse(content.trim());
      } catch (e) {
        // Se falhar, tenta extrair JSON de blocos de código
        const jsonMatch = content.match(/```(?:json)?\n?([\s\S]*?)\n?```/) || 
                         content.match(/\[[\s\S]*?\]/);
        if (jsonMatch) {
          try {
            jsonData = JSON.parse(jsonMatch[1] || jsonMatch[0]);
          } catch (e2) {
            throw new Error(`Erro ao parsear JSON extraído: ${e2.message}`);
          }
        } else {
          throw new Error(`Resposta não contém JSON válido. Conteúdo: ${content.substring(0, 200)}...`);
        }
      }
      
      // Valida se é um array de leads
      if (Array.isArray(jsonData) && jsonData.length > 0) {
        // Valida cada lead
        const validLeads = jsonData.filter(lead => 
          lead && 
          typeof lead === 'object' && 
          (lead.nome || lead.name) && 
          (lead.telefone || lead.phone)
        );
        
        if (validLeads.length === 0) {
          throw new Error('Nenhum lead válido encontrado na resposta');
        }
        
        updateUsage(key, model, true);
        console.log(`generateLeads: Sucesso na tentativa ${attempts} com ${model}. Duração: ${(Date.now() - startTime) / 1000}s. ${validLeads.length} leads válidos gerados.`);
        return JSON.stringify(validLeads);
      } else {
        throw new Error(`Resposta inválida: esperado array, recebido ${typeof jsonData}. Conteúdo: ${JSON.stringify(jsonData).substring(0, 200)}...`);
      }

    } catch (error: any) {
      console.error(`generateLeads: Erro na tentativa ${attempts} com ${model} (Chave: ${key.substring(0,10)}...):`, error.message);
      updateUsage(key, model, false);
      lastError = error;
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        console.warn(`Rate limit atingido para ${model} com chave ${key.substring(0,10)}. Aguardando mais tempo...`);
        // Aguarda mais tempo em caso de rate limit
        await new Promise(resolve => setTimeout(resolve, 10000 + (attempts * 5000)));
      } else if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 404)) {
        console.warn(`Chave/modelo inválido: ${model} com chave ${key.substring(0,10)}. Marcando como problemático.`);
        // Marca a chave como problemática temporariamente
        const trackerKey = `${key}-${model}`;
        if (usageTracker[trackerKey]) {
          usageTracker[trackerKey].consecutiveErrors = 5; // Força a não usar por um tempo
        }
      }
      if (attempts >= maxAttempts) {
        console.error("generateLeads: Todas as tentativas internas falharam.");
        break; 
      }
      // Backoff progressivo mais agressivo
      const delay = Math.min(3000 * Math.pow(1.5, attempts), 15000);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw lastError;
};